import type { XviewPerfTimingRegistry } from "./firstApiTiming";

export type NamedScriptStrategy =
  | "first-start"
  | "first-complete"
  | "last-quiet";

export interface NamedScriptInfo {
  url: string;
  startTime: number;
  responseEnd: number;
  strategy: NamedScriptStrategy;
  chunkCount: number;
}

export interface NamedScriptLoadOptions {
  pattern?: RegExp;
  strategy?: NamedScriptStrategy;
  quietWindowMs?: number;
  bufferSize?: number;
  onLoad?: (info: NamedScriptInfo) => void;
}

export interface NamedScriptCoreConfig {
  defaultPattern: RegExp;
  hasStored: (ns: XviewPerfTimingRegistry) => boolean;
  store: (
    ns: XviewPerfTimingRegistry,
    chunk: { url: string; startTime: number; responseEnd: number },
  ) => void;
}

const DEFAULT_BUFFER_SIZE = 500;
const DEFAULT_STRATEGY: NamedScriptStrategy = "last-quiet";
const DEFAULT_QUIET_WINDOW_MS = 500;

export function observeNamedScript(
  options: NamedScriptLoadOptions,
  config: NamedScriptCoreConfig,
): void {
  if (typeof window === "undefined") return;
  if (typeof PerformanceObserver === "undefined") return;

  const pattern = options.pattern ?? config.defaultPattern;
  const bufferSize = options.bufferSize ?? DEFAULT_BUFFER_SIZE;
  const strategy = options.strategy ?? DEFAULT_STRATEGY;
  const quietWindowMs = options.quietWindowMs ?? DEFAULT_QUIET_WINDOW_MS;

  try {
    performance.setResourceTimingBufferSize?.(bufferSize);
  } catch {
    /* no-op */
  }

  const ns = (window.__xviewPerfTiming__ ??= {} as XviewPerfTimingRegistry);
  if (config.hasStored(ns)) return;

  const matches = (e: PerformanceResourceTiming): boolean => {
    if (e.initiatorType !== "script") return false;
    const exclude = new Set(ns.excludedInstrumentationScriptUrls ?? []);
    if (exclude.has(e.name)) return false;
    return pattern.test(e.name);
  };

  let observer: PerformanceObserver | null = null;
  let chunkCount = 0;
  let bestChunk: PerformanceResourceTiming | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  const finish = (chunk: PerformanceResourceTiming): void => {
    if (config.hasStored(ns)) return;
    config.store(ns, {
      url: chunk.name,
      startTime: chunk.startTime,
      responseEnd: chunk.responseEnd,
    });

    try {
      options.onLoad?.({
        url: chunk.name,
        startTime: chunk.startTime,
        responseEnd: chunk.responseEnd,
        strategy,
        chunkCount,
      });
    } catch {
      /* user callback errors are isolated */
    }

    observer?.disconnect();
    observer = null;
    if (debounceTimer != null) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
  };

  const handleFirstStart = (cands: PerformanceResourceTiming[]): void => {
    const earliest = cands.reduce((a, b) =>
      a.startTime <= b.startTime ? a : b,
    );
    finish(earliest);
  };

  const handleFirstComplete = (cands: PerformanceResourceTiming[]): void => {
    const earliest = cands.reduce((a, b) =>
      a.responseEnd <= b.responseEnd ? a : b,
    );
    finish(earliest);
  };

  const handleLastQuiet = (cands: PerformanceResourceTiming[]): void => {
    for (const e of cands) {
      if (bestChunk == null || e.responseEnd > bestChunk.responseEnd) {
        bestChunk = e;
      }
    }
    if (debounceTimer != null) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      if (bestChunk != null) finish(bestChunk);
    }, quietWindowMs);
  };

  const installObserver = (init: PerformanceObserverInit): boolean => {
    try {
      observer = new PerformanceObserver((list) => {
        const cands = (list.getEntries() as PerformanceResourceTiming[]).filter(
          matches,
        );
        if (cands.length === 0) return;
        chunkCount += cands.length;

        if (strategy === "first-start") {
          handleFirstStart(cands);
        } else if (strategy === "first-complete") {
          handleFirstComplete(cands);
        } else {
          handleLastQuiet(cands);
        }
      });
      observer.observe(init);
      return true;
    } catch {
      observer = null;
      return false;
    }
  };

  if (!installObserver({ type: "resource", buffered: true })) {
    installObserver({ entryTypes: ["resource"] });
  }
}
